import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import * as R from 'ramda'
import { t } from 'ttag'

import { deleteData, patchData, postData } from 'api/orm'
import {
  DELETE_CANDIDATES,
  DELETE_JOBS,
  SET_ARCHIVED_JOBS,
  IMPORT_CANDIDATES,
  MOVE_CANDIDATES,
  SELECT_CANDIDATES,
  SEND_MESSAGES,
} from 'ducks/concurrency'
import { getLocation } from 'ducks/router'
import { candidates, jobs } from 'orm'

const getStatus = tab => {
  const statuses = {
    overview: null,
    invites: ['created', 'invited'],
    tests: ['completed'],
    interviews: ['screened'],
    results: ['interviewed'],
  }

  return statuses[tab]
}

export default function* rootSaga() {
  yield takeLatest(DELETE_JOBS, deleteJobsSaga)
  yield takeLatest(SET_ARCHIVED_JOBS, setArchivedJobsSaga)
  yield takeLatest(DELETE_CANDIDATES, deleteCandidatesSaga)
  yield takeLatest(IMPORT_CANDIDATES, importCandidatesSaga)
  yield takeLatest(SELECT_CANDIDATES, selectCandidatesSaga)
  yield takeLatest(SEND_MESSAGES, sendMessagesSaga)
  yield takeLatest(MOVE_CANDIDATES, moveCandidatesSaga)
}

function* deleteJobsSaga(action) {
  const {
    payload: { onShowSnackbar },
  } = action

  const { projects } = yield select(state => state.orm)
  const { query = {} } = yield select(getLocation)

  const pks = R.keys(projects?.meta?.selected) || []

  const data = yield all(R.map(id => call(deleteData, 'projects', id), pks))

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onShowSnackbar({
      message: t`${count} job positions were successfully deleted.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    onShowSnackbar({
      message: t`${count} job positions were not deleted.`,
      type: 'error',
    })
  }

  yield put(jobs.fetchStart(query))
}

function* setArchivedJobsSaga(action) {
  const {
    payload: { onShowSnackbar, archived },
  } = action

  const { projects } = yield select(state => state.orm)
  const { query = {} } = yield select(getLocation)

  const pks = R.keys(projects?.meta?.selected) || []

  const data = yield all(
    R.map(id => call(patchData, 'projects', id, { active: archived }), pks),
  )

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onShowSnackbar({
      message: archived
        ? t`${count} job positions were successfully unarchived.`
        : t`${count} job positions were successfully archived.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    onShowSnackbar({
      message: archived
        ? t`${count} job positions were not unarchived.`
        : t`${count} job positions were not archived.`,
      type: 'error',
    })
  }

  yield put(jobs.fetchStart(query))
}

function* deleteCandidatesSaga(action) {
  const {
    payload: { projectId, status, onShowSnackbar },
  } = action

  const { candidates: applicants } = yield select(state => state.orm)
  const { query = {} } = yield select(getLocation)

  const pks = R.keys(applicants?.meta?.selected) || []

  const data = yield all(R.map(id => call(deleteData, 'candidates', id), pks))

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onShowSnackbar({
      message: t`${count} candidates were successfully deleted.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    onShowSnackbar({
      message: t`${count} candidates positions were not deleted.`,
      type: 'error',
    })
  }

  yield put(candidates.fetchStart({ project: projectId, status, ...query }))
}

function* importCandidatesSaga(action) {
  const {
    payload: {
      candidates: applicants,
      projectId,
      status,
      onShowSnackbar,
      onSuccess,
    },
  } = action

  const items = R.values(yield select(candidates.getItems))
  const { query = {} } = yield select(getLocation)

  let duplicates = 0

  const data = yield all(
    R.pipe(
      R.filter(({ email }) => {
        // TODO: it should be validated on the BE instead
        const isDuplicated = R.find(R.propEq('email', email))(items)

        if (isDuplicated) {
          duplicates = duplicates + 1
        }

        return R.not(isDuplicated)
      }),
      R.map(({ email, name, phone }) =>
        call(postData, 'candidates', undefined, undefined, {
          email,
          name,
          phone,
          project: projectId,
        }),
      ),
    )(applicants),
  )

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(duplicates, 0)) {
    onShowSnackbar({
      message: t`${duplicates} candidates were not imported because they already exist.`,
      type: 'warning',
    })
  }

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onSuccess()

    onShowSnackbar({
      message: t`${count} candidates were successfully imported.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    const failedCandidates = data.reduce(
      (acc, candidate, index) =>
        candidate.error ? [...acc, applicants[index].name] : acc,
      [],
    )

    onShowSnackbar({
      message:
        t`${count} candidates were not imported:` +
        ` ${failedCandidates.join(', ')}`,
      type: 'error',
      timeout: 9 ** 9,
    })
  }

  yield put(candidates.fetchStart({ project: projectId, status, ...query }))
}

function* selectCandidatesSaga(action) {
  const {
    payload: { projectId, onShowSnackbar },
  } = action

  const { candidates: applicants } = yield select(state => state.orm)
  const { query = {} } = yield select(getLocation)

  const pks = R.keys(applicants?.meta?.selected) || []

  const data = yield all(
    R.map(id => call(patchData, 'candidates', id, { status: 'screened' }), pks),
  )

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onShowSnackbar({
      message: t`${count} candidates were successfully moved.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    onShowSnackbar({
      message: t`${count} candidates were not moved.`,
      type: 'error',
    })
  }

  yield put(
    candidates.fetchStart({
      project: projectId,
      status: ['completed'],
      ...query,
    }),
  )
}

function* sendMessagesSaga(action) {
  const {
    payload: {
      candidates: applicants,
      projectId,
      workspaceId,
      onShowSnackbar,
      onSuccess,
    },
  } = action

  const { query = {} } = yield select(getLocation)

  const data = yield all(
    R.map(
      ({ deadline, key, kind, receiver, sender, subject, templateId }) =>
        call(postData, 'candidates', key, 'invite', {
          receiver_name: receiver,
          sender_name: sender,
          deadline,
          kind,
          subject,
          templateId,
        }),
      applicants,
    ),
  )

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onSuccess()

    onShowSnackbar({
      message: t`${count} candidates were successfully messaged.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    onShowSnackbar({
      message: t`${count} candidates were not messaged.`,
      type: 'error',
    })
  }

  const fetchPayload = workspaceId
    ? {
      ...query,
      talent_assessment_workspace: workspaceId,
      status: ['created', 'invited'],
    }
    : {
      ...query,
      project: projectId,
      status: ['created', 'invited'],
    }

  yield put(candidates.fetchStart(fetchPayload))
}

function* moveCandidatesSaga(action) {
  const {
    payload: {
      candidates: applicants,
      project,
      status,
      onShowSnackbar,
      onSuccess,
    },
  } = action

  const {
    payload: { id, tab },
    query = {},
  } = yield select(getLocation)

  const data = yield all(
    R.map(
      id => call(patchData, 'candidates', id, { project, status }),
      applicants,
    ),
  )

  const success = R.filter(({ error }) => R.isNil(error), data)
  const failure = R.filter(({ error }) => R.not(R.isNil(error)), data)

  if (R.gt(R.length(success), 0)) {
    const count = R.length(success)

    onSuccess()

    onShowSnackbar({
      message: t`${count} candidates were successfully moved.`,
      type: 'success',
    })
  }

  if (R.gt(R.length(failure), 0)) {
    const count = R.length(failure)

    onShowSnackbar({
      message: t`${count} candidates were not moved.`,
      type: 'error',
    })
  }

  yield put(
    candidates.fetchStart({
      project: id,
      status: getStatus(tab),
      ...query,
    }),
  )
}
